import { useEffect, useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { addTranslation, deleteTranslation, updateTranslation } from './api';
import { type DraftRow } from './messageLibraryModel';
import type { MessageTranslation } from './types';
import './messageLibrary.css';

/**
 * The detail's Translations tab — this message's translations, edited in place.
 *
 * Click-to-edit, the selection checkboxes and the required-yet-empty tint all
 * come from the shared `<AppDataGrid>` (`edit` + `onCellCommit` + `selectable`,
 * standing rule 7) — nothing about the grid is re-implemented here.
 *
 * ── Two modes, because a child row needs a parent ─────────────────────────────
 * On a SAVED message each row saves on its own through the per-translation
 * endpoints that already exist, so a translation change is audited the way
 * anyone reading the audit log expects.
 *
 * On a NEW message there is no id to hang a translation off yet, so rows are
 * STAGED in the parent and written immediately after the message is created.
 * That is the only honest option — posting a child to a parent that does not
 * exist cannot work — and it is what the prototype's "saved with the message"
 * describes. The hint text says which mode you are in rather than leaving you
 * to find out by pressing Cancel.
 */

const DRAFT_PREFIX = 'draft-';

interface Props {
  /** The message these belong to, or `null` while it is still being created. */
  messageId: string | null;
  /** Saved translations, or — when creating — the rows staged so far. */
  translations: MessageTranslation[];
  /** The languages the API accepts, so the Language cell offers exactly those. */
  languages: string[];
  /** Re-read the record after a change (saved message only). */
  onChanged: () => void;
  /** Hand the staged rows back to the parent (new message only). */
  onDraftChange?: (rows: DraftRow[]) => void;
}

export function TranslationsTab({
  messageId, translations, languages, onChanged, onDraftChange,
}: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const creating = messageId === null;

  // While CREATING the parent owns the rows, so this component is controlled and
  // holds no copy — two sources for one list is how they drift apart.
  const [saved, setSaved] = useState<DraftRow[]>(translations);
  const rows = creating ? (translations as DraftRow[]) : saved;

  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);

  // A saved record can reload under us (save, refresh, prev/next), so the
  // server's view wins whenever it changes.
  useEffect(() => {
    if (creating) return;
    setSaved(translations);
    setSelected(new Set());
  }, [translations, creating]);

  const publish = (next: DraftRow[]) => {
    if (creating) onDraftChange?.(next);
    else setSaved(next);
  };

  /** A language may carry only ONE translation, so a taken one is not offered
   *  again — except on the row that already holds it. */
  const languagesFor = (row: DraftRow) => {
    const taken = new Set(rows.filter((r) => r.oid !== row.oid).map((r) => r.language ?? ''));
    return languages.filter((l) => !taken.has(l));
  };

  const spare = useMemo(() => {
    const taken = new Set(rows.map((r) => r.language ?? ''));
    return languages.filter((l) => !taken.has(l));
  }, [rows, languages]);

  const addRow = () => {
    if (spare.length === 0) {
      notify.warning('Every language already has a translation for this message.');
      return;
    }
    publish([
      ...rows,
      {
        oid: `${DRAFT_PREFIX}${Date.now()}`,
        language: null,
        translation: null,
        lastUpdatedDate: null,
        draft: true,
      },
    ]);
  };

  const removeRows = async () => {
    const victims = rows.filter((r) => selected.has(r.oid));
    if (victims.length === 0) {
      notify.warning('Select at least one translation to remove.');
      return;
    }

    const persisted = victims.filter((r) => !r.draft);
    const ok = await confirm({
      title: 'Remove translation?',
      message: victims.length === 1
        ? 'Remove this translation?'
        : `Remove ${victims.length} translations?`,
      tone: 'danger',
    });
    if (!ok) return;

    setBusy(true);
    try {
      // Staged rows never reached the server, so they just disappear.
      if (messageId) for (const row of persisted) await deleteTranslation(messageId, row.oid);
      publish(rows.filter((r) => !selected.has(r.oid)));
      setSelected(new Set());
      if (persisted.length > 0) {
        notify.success('Deleted.');
        onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  /** Commit one cell. A row is written only once BOTH cells are filled. */
  const commit = async (row: DraftRow, column: AppColumn<DraftRow>, value: string) => {
    const field = column.field as 'language' | 'translation';
    const next: DraftRow = { ...row, [field]: value };
    publish(rows.map((r) => (r.oid === row.oid ? next : r)));

    // While creating, the parent writes the whole set after the message exists.
    if (creating || !messageId) return;

    const language = (next.language ?? '').trim();
    const translation = (next.translation ?? '').trim();
    if (!language || !translation) return;   // still incomplete — nothing to save yet

    setBusy(true);
    try {
      if (next.draft) await addTranslation(messageId, language, translation);
      else await updateTranslation(messageId, next.oid, language, translation);
      notify.success('Saved successfully.');
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const columns: AppColumn<DraftRow>[] = [
    {
      field: 'language',
      header: 'Language *',
      width: 200,
      render: (r) => r.language ?? '',
      edit: {
        type: 'combo',
        trigger: 'click',
        options: (r) => languagesFor(r),
        requiredEmpty: (r) => !r.language,
      },
    },
    {
      field: 'translation',
      header: 'Translation *',
      width: 520,
      render: (r) => r.translation ?? '',
      edit: {
        type: 'text',
        trigger: 'click',
        requiredEmpty: (r) => !r.translation,
      },
    },
  ];

  return (
    <>
      <div className="ml-tr-bar">
        <button
          type="button"
          className="ml-tr-btn add"
          onClick={addRow}
          disabled={busy}
          title="Add a translation"
          aria-label="Add a translation"
        >
          <i className="ti ti-plus" />
        </button>
        <button
          type="button"
          className="ml-tr-btn del"
          onClick={() => void removeRows()}
          disabled={busy}
          title="Remove the selected translations"
          aria-label="Remove the selected translations"
        >
          <i className="ti ti-minus" />
        </button>
        <span className="ml-tr-hint">
          Click a cell to edit. One translation per language.{' '}
          {creating
            ? 'These are saved with the message, so Cancel discards them.'
            : 'Each row saves on its own, so removing or editing one takes effect immediately — Cancel on the message does not undo it.'}
        </span>
      </div>

      <AppDataGrid<DraftRow>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.oid}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        onCellCommit={(r, c, v) => void commit(r, c, v)}
        maximizable
        exportFilename="message-translations"
        defaultPageSize={10}
        emptyText="No data to display."
      />
    </>
  );
}

export default TranslationsTab;
